import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronDown, Clock, LayoutDashboard, Search, Star } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as KeyboardEventReact, type ReactNode } from 'react';
import { api } from '../src/api/cliente';
import { semAcento } from '../src/navegacao/busca';
import { useMenu, type ItemMenu } from '../src/navegacao/indice';
import { usePreCarregar } from '../src/navegacao/preCarregar';
import { useResumoDia, type PendenciaDia } from './ResumoDia';
import type { ResumoInicio } from './TelaInicial';

/** Ordem do trabalho, não do alfabeto: vender e comprar primeiro, cadastro e configuração por último. Módulo novo entra no fim. */
const ORDEM = ['FAT', 'COM', 'EST', 'FIN', 'FIS', 'CAD', 'CFG'];

/** "Cadastros Gerais" vira "Cadastros" na barra; o nome inteiro fica no título e no rótulo acessível. */
export function nomeCurto(nome: string) {
  return nome.split(/\s+/)[0] ?? nome;
}

export function ordenarModulos<T extends { codigo: string }>(modulos: T[]): T[] {
  const pos = (c: string) => {
    const i = ORDEM.indexOf(c.toUpperCase());
    return i < 0 ? ORDEM.length : i;
  };
  return [...modulos].sort((a, b) => pos(a.codigo) - pos(b.codigo));
}

export function filtrarMenu(itens: ItemMenu[], termo: string): ItemMenu[] {
  if (!termo) return itens;
  return itens.flatMap((i) => {
    if (i.tipo === 'rotina') return semAcento(`${i.codigo ?? ''} ${i.nome}`).includes(termo) ? [i] : [];
    if (i.tipo === 'separador') return [];
    const filhos = filtrarMenu(i.itens, termo);
    return filhos.length ? [{ ...i, itens: filhos }] : [];
  });
}

/** Pendências do dia por rotina (soma quando o resumo aponta duas coisas para a mesma rotina). */
function porRotina(pendencias: PendenciaDia[]) {
  const m = new Map<string, { quantidade: number; alerta: boolean }>();
  for (const p of pendencias) {
    const atual = m.get(p.rotina) ?? { quantidade: 0, alerta: false };
    m.set(p.rotina, { quantidade: atual.quantidade + p.quantidade, alerta: atual.alerta || !!p.alerta });
  }
  return m;
}

/** Foco por seta entre os links do menu (o filtro conta como o primeiro). */
function moverFoco(painel: HTMLElement | null, passo: number) {
  if (!painel) return;
  const alvos = [...painel.querySelectorAll<HTMLElement>('.menu-modulo-filtro input, a.menu-rotina, a.menu-atalho')];
  const i = alvos.indexOf(document.activeElement as HTMLElement);
  alvos[Math.max(0, Math.min(alvos.length - 1, i + passo))]?.focus();
}

/**
 * Barra de módulos (ADR-0008, revisão 3 — abas de trabalho): os módulos ficam no topo; o clique abre o menu do módulo inteiro,
 * em colunas, com filtro já focado, favoritos e as abertas há pouco do lado. Escolher uma rotina abre a aba dela embaixo.
 */
export function BarraModulos({ moduloAtivo }: { moduloAtivo: string | undefined }) {
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const resumo = useQuery({ queryKey: ['inicio'], queryFn: () => api.get<ResumoInicio>('/inicio/resumo') });
  const [aberto, setAberto] = useState<string | null>(null);
  const barra = useRef<HTMLElement>(null);
  const modulos = useMemo(() => ordenarModulos(resumo.data?.modulos ?? []), [resumo.data]);

  // Qualquer navegação fecha o menu (escolheu a rotina, voltou ao início…).
  const [caminhoVisto, setCaminhoVisto] = useState(caminho);
  if (caminhoVisto !== caminho) {
    setCaminhoVisto(caminho);
    setAberto(null);
  }

  const fechar = (devolverFoco: boolean) => {
    const codigo = aberto;
    setAberto(null);
    if (devolverFoco && codigo) barra.current?.querySelector<HTMLElement>(`[data-modulo="${codigo}"]`)?.focus();
  };

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e: PointerEvent) => {
      if (!barra.current?.contains(e.target as Node)) setAberto(null);
    };
    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
  }, [aberto]);

  // Setas andam entre os módulos; seta para baixo abre o menu do módulo em foco.
  const teclaNaBarra = (e: KeyboardEventReact<HTMLUListElement>) => {
    const botoes = [...(barra.current?.querySelectorAll<HTMLElement>('.modulo-botao') ?? [])];
    const i = botoes.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const proximo = botoes[(i + (e.key === 'ArrowRight' ? 1 : -1) + botoes.length) % botoes.length];
      proximo.focus();
      if (aberto && proximo.dataset.modulo) setAberto(proximo.dataset.modulo);
    } else if (e.key === 'ArrowDown' && botoes[i].dataset.modulo) {
      e.preventDefault();
      setAberto(botoes[i].dataset.modulo!);
    }
  };

  return (
    <nav className="modulos" aria-label="Módulos" ref={barra}>
      <ul className="modulos-lista" onKeyDown={teclaNaBarra}>
        <li>
          <Link to="/" className="modulo-botao" data-ativo={caminho === '/' || undefined} aria-current={caminho === '/' ? 'page' : undefined}>
            Início
          </Link>
        </li>
        {modulos.map((m) => {
          const codigo = m.codigo.toUpperCase();
          return (
            <li key={codigo}>
              <button type="button" className="modulo-botao" data-modulo={codigo} data-ativo={codigo === moduloAtivo || undefined}
                aria-expanded={aberto === codigo} aria-controls={aberto === codigo ? 'menu-modulo' : undefined} aria-haspopup="true"
                title={`${m.nome} · ${codigo}`} aria-label={m.nome}
                onClick={() => setAberto((a) => (a === codigo ? null : codigo))}
                onPointerEnter={() => aberto && aberto !== codigo && setAberto(codigo)}>
                {nomeCurto(m.nome)}
                <ChevronDown size={13} strokeWidth={2} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {aberto && (
        <MenuDoModulo key={aberto} codigo={aberto} nome={modulos.find((m) => m.codigo.toUpperCase() === aberto)?.nome ?? aberto}
          resumo={resumo.data} aoFechar={fechar} />
      )}
    </nav>
  );
}

interface PropsMenu {
  codigo: string;
  nome: string;
  resumo: ResumoInicio | undefined;
  aoFechar: (devolverFoco: boolean) => void;
}

function MenuDoModulo({ codigo, nome, resumo, aoFechar }: PropsMenu) {
  const menu = useMenu(codigo);
  const dia = useResumoDia([codigo], codigo);
  const preCarregar = usePreCarregar();
  const navegar = useNavigate();
  const rotinaAtiva = useRouterState({ select: (s) => /^\/r\/([A-Z0-9]+)/.exec(s.location.pathname)?.[1] });
  const painel = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const [filtro, setFiltro] = useState('');
  const termo = semAcento(filtro.trim());
  const grupos = useMemo(() => filtrarMenu(menu.data?.grupos ?? [], termo), [menu.data, termo]);
  const pendentes = useMemo(() => porRotina(dia.pendencias), [dia.pendencias]);

  useEffect(() => campo.current?.focus(), []);

  const doModulo = (m: string) => m.toUpperCase() === codigo;
  const favoritos = (resumo?.favoritos ?? []).filter((f) => doModulo(f.modulo));
  const recentes = (resumo?.recentes ?? []).filter((r) => doModulo(r.modulo)).slice(0, 5);

  const tecla = (e: KeyboardEventReact<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      aoFechar(true);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moverFoco(painel.current, e.key === 'ArrowDown' ? 1 : -1);
    }
  };
  // Enter no filtro abre a primeira rotina que sobrou.
  const enterNoFiltro = (e: KeyboardEventReact<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const primeira = painel.current?.querySelector<HTMLAnchorElement>('.menu-colunas a.menu-rotina');
    if (primeira?.dataset.rotina) {
      e.preventDefault();
      void navegar({ to: '/r/$rotina', params: { rotina: primeira.dataset.rotina } });
    }
  };

  const linkRotina = (rotina: string, rotulo: string, chave: string, icone?: ReactNode) => {
    const p = pendentes.get(rotina);
    return (
      <li key={chave}>
        <Link to="/r/$rotina" params={{ rotina }} className={icone ? 'menu-atalho' : 'menu-rotina'} data-rotina={rotina}
          data-ativa={rotina === rotinaAtiva || undefined} aria-current={rotina === rotinaAtiva ? 'page' : undefined}
          title={`${rotulo} · ${rotina}`} {...preCarregar(rotina)}>
          {icone ?? <span className="mono menu-codigo">{rotina}</span>}
          <span className="menu-nome">{rotulo}</span>
          {p && <span className="menu-contagem" data-alerta={p.alerta || undefined} aria-label={`${p.quantidade} para resolver`}>{p.quantidade}</span>}
        </Link>
      </li>
    );
  };

  const itens = (lista: ItemMenu[]): ReactNode => (
    <ul className="menu-lista">
      {lista.map((i, n) => {
        if (i.tipo === 'separador') return <li key={`s${n}`} className="menu-separador" aria-hidden="true" />;
        if (i.tipo === 'rotina' && i.codigo) return linkRotina(i.codigo, i.nome, i.codigo);
        return (
          <li key={i.codigo ?? i.nome} className="menu-sub">
            <span className="menu-subtitulo">{i.nome}</span>
            {itens(i.itens)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="menu-modulo" id="menu-modulo" ref={painel} role="region" aria-label={`Menu de ${nome}`} onKeyDown={tecla}>
      <aside className="menu-lado">
        <Link to="/m/$modulo" params={{ modulo: codigo.toLowerCase() }} className="menu-painel">
          <LayoutDashboard size={14} strokeWidth={1.75} aria-hidden="true" />
          Painel de {nome}
        </Link>
        <h3 className="menu-etiqueta">Favoritas</h3>
        {favoritos.length ? (
          <ul className="menu-lista">{favoritos.map((f) => linkRotina(f.codigo, f.nome, `f${f.codigo}`, <Star size={13} strokeWidth={1.75} aria-hidden="true" />))}</ul>
        ) : (
          <p className="menu-vazio">Marque com ★ (Ctrl+D) as rotinas que você mais usa.</p>
        )}
        <h3 className="menu-etiqueta">Abertas há pouco</h3>
        {recentes.length ? (
          <ul className="menu-lista">{recentes.map((r) => linkRotina(r.codigo, r.nome, `r${r.codigo}`, <Clock size={13} strokeWidth={1.75} aria-hidden="true" />))}</ul>
        ) : (
          <p className="menu-vazio">Nenhuma rotina deste módulo ainda.</p>
        )}
      </aside>
      <div className="menu-principal">
        <label className="menu-modulo-filtro">
          <Search size={15} strokeWidth={1.75} aria-hidden="true" />
          <input ref={campo} type="search" value={filtro} onChange={(e) => setFiltro(e.target.value)} onKeyDown={enterNoFiltro}
            placeholder={`Filtrar rotinas de ${nome} por nome ou código`} aria-label={`Filtrar rotinas de ${nome}`} autoComplete="off" spellCheck={false} />
        </label>
        {menu.isLoading ? (
          <div className="esqueleto" style={{ height: 160 }} />
        ) : grupos.length ? (
          <div className="menu-colunas">
            {grupos.map((g) => (
              <section key={g.codigo ?? g.nome} className="menu-grupo" aria-label={g.nome}>
                <h3 className="menu-etiqueta">{g.nome}</h3>
                {g.tipo === 'rotina' && g.codigo ? <ul className="menu-lista">{linkRotina(g.codigo, g.nome, g.codigo)}</ul> : itens(g.itens)}
              </section>
            ))}
          </div>
        ) : (
          <p className="menu-vazio">Nenhuma rotina de {nome} com “{filtro.trim()}”. Ctrl+K procura em todos os módulos.</p>
        )}
        <footer className="menu-rodape" aria-hidden="true">
          <span><kbd>↑</kbd> <kbd>↓</kbd> navegar</span>
          <span><kbd>Enter</kbd> abrir em aba</span>
          <span><kbd>Esc</kbd> fechar</span>
          <span><kbd>Ctrl</kbd> <kbd>K</kbd> busca em tudo</span>
        </footer>
      </div>
    </div>
  );
}
